/**
 * SafeCurves legend helpers — wired into `safecurvesConfig.parseLegendLayers`.
 *
 * `parseLegendLayers` converts the SafeCurves `ISafeCurvesSystemLayers`
 * envelope into the `MapLayer[]` array consumed by `legendStore`.
 */

import type { ILayerSource } from '#lib/types.js';
import {
	EIdentify,
	EPrioritize,
	EInterventions,
	ESafeCurvesModule,
	type ISafeCurvesSystemLayers
} from './interfaces';
import type { ICrashesSystemLayers } from '#lib/apps/safesystem/context/interfaces.js';
import { mapPriorityDescription } from './prioritize';
import { moduleTable } from './layerConfig';
import type { MapLayer } from '#lib/stores/legendStore.svelte.js';
import { SIGN_LEVELS, signSpeed } from './legends/signImages';
import {
	buildLegendLayers,
	parseMatchPaint,
	parseSinglePaint,
	type Parsers
} from '../legendHelpers';

function parseImageItems(
	feature: ILayerSource,
	filterAttr: string,
	items: readonly (readonly [string, string])[]
) {
	return {
		filterAttr,
		defaultEnabled: false,
		legendItems: items.map(([label, image]) => ({
			legendItemId: `${feature.id}:${label.replace(' ', '_')}`,
			filterValue: label,
			label,
			image,
			enabled: false
		}))
	};
}

const parseSigns = (f: ILayerSource) =>
	parseImageItems(f, 'advisory_sign_recommendation', SIGN_LEVELS);

// A single unfiltered item, for symbol/fill layers without a colour to read from the paint.
const onOff = (f: ILayerSource, label: string) => ({
	filterAttr: '',
	defaultEnabled: false,
	legendItems: [{ legendItemId: `${f.id}:all`, label, enabled: false }]
});

export const pffaLabelFor = (raw: string) => {
	if (raw === 'None') return 'Adjacent lane (TP to TP)';
	if (raw === 'Maximum') return 'Aggressive deceleration';
	return `${raw} deceleration`;
};

const parsePrioritize = (f: ILayerSource) =>
	parseMatchPaint(f, 'line-color', {
		defaultEnabled: true,
		labelFor: (raw) => mapPriorityDescription[raw],
		idSuffixFor: (raw) => mapPriorityDescription[raw].replace(' ', '_')
	});

const parsers: Parsers = {
	[EIdentify.Identify]: (f) => parseMatchPaint(f, 'line-color'),
	[EPrioritize.Prioritize]: parsePrioritize,
	// Simplified corridors mirror Prioritize's items + filterAttr; no legend UI of their own.
	[EPrioritize.PrioritizeDisplayOnly]: parsePrioritize,
	[EInterventions.CurveAdvisorySigns]: parseSigns,
	// Halo mirrors the signs' items + filterAttr so syncVisibleLayers applies the same filter; it has no legend UI of its own.
	[EInterventions.CurveAdvisorySignsHalo]: parseSigns,
	[EInterventions.CurveAdvisorySpeeds]: (f) =>
		parseImageItems(
			f,
			'advisory_sign_recommendation',
			SIGN_LEVELS.map(([label]) => [label, signSpeed])
		),
	[EInterventions.PavementFrictionFocusAreas]: (f) =>
		parseMatchPaint(f, 'line-color', {
			labelFor: pffaLabelFor,
			idSuffixFor: (raw) => `hfst:${raw.replace(' ', '_')}`
		}),
	[EInterventions.SpeedLimitReview]: (f) =>
		parseMatchPaint(f, 'line-color', {
			idSuffixFor: (raw) => `speedlimitreview:${raw.replace(' ', '_')}`
		}),
	[EInterventions.PassingSightDistance]: (f) =>
		parseSinglePaint(f, 'line-color', 'passingsightdistance', 'No passing lines (visibility)'),
	[EInterventions.StoppingSightDistance]: (f) =>
		parseSinglePaint(f, 'line-color', 'stoppingsightdistance', 'Insufficient SSD - light vehicles'),
	[EInterventions.SSDObstructions]: (f) =>
		parseSinglePaint(f, 'fill-color', 'ssdobstructions', 'Obstruction'),
	[EInterventions.ClearZone]: (f) =>
		parseMatchPaint(f, 'line-color', {
			idSuffixFor: (raw) => `clearzone:${raw.replace(' ', '_')}`
		}),
	[EPrioritize.ProgramProjects]: (f) => parseMatchPaint(f, 'line-color'),
	[EInterventions.ExistingSigns]: (f) => onOff(f, 'Existing sign'),
	[EInterventions.ExistingSpeedPlaques]: (f) => onOff(f, 'Existing speed plaque'),
	[EInterventions.ExistingChevronAreas]: (f) => onOff(f, 'Existing chevrons'),
	[EInterventions.ExistingBarriers]: (f) =>
		parseSinglePaint(f, 'line-color', 'existingbarriers', 'Existing barrier')
};

export function parseLegendLayers(
	payload: ISafeCurvesSystemLayers & ICrashesSystemLayers
): MapLayer[] {
	// SafeCurves defaults to showing Prioritize; when that module's data is
	// absent, fall back to enabling every available SafeCurves layer so the map
	// isn't blank. Crash overlays keep their own per-parser defaults.
	const prioritizeAvailable = Boolean(
		payload[ESafeCurvesModule.Prioritize]?.[EPrioritize.Prioritize]
	);
	const modules: string[] = Object.values(ESafeCurvesModule);
	// Programs layers carry none of the map-data-filter attributes and stay off until toggled.
	// Built here, not at module scope: layerConfig → columns → this file is an import cycle.
	const localLayerIds = new Set(
		Object.values(moduleTable).flatMap((m) =>
			Object.entries(m.layers).flatMap(([id, l]) => ('local' in l ? [id] : []))
		)
	);

	return buildLegendLayers(payload, modules, parsers, (module, defaultEnabled, layerId) => {
		const own = modules.includes(module) && !localLayerIds.has(layerId);
		// Crash and simplified-Prioritize tiles lack the mapDataFilter attributes, so they opt out of that filter.
		return {
			enabled: !prioritizeAvailable && own ? true : defaultEnabled,
			excludeFromMapDataFilter: !own || layerId === EPrioritize.PrioritizeDisplayOnly
		};
	});
}
